import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { FlyControls } from 'three/examples/jsm/controls/FlyControls.js';
import GUI from 'lil-gui';
import Stats from 'stats.js';

// Types and Configs
type WeatherType = 'Clear' | 'Rain';
type BuildingType = 'police' | 'government' | 'commercial' | 'residential' | 'industrial';

interface CityConfig {
  seed: number;
  gridBlocks: number;     // number of blocks per axis (NxN)
  blockSize: number;      // size of a block (buildings/parks)
  roadWidth: number;      // width of roads
  sidewalkWidth: number;  // width of sidewalks
  carCount: number;       // Reduced to 20 for performance
  pedestrianCount: number;
  petCount: number;
  taxiFraction: number;
  policeFraction: number;
  ambulanceFraction: number;
  deliveryFraction: number;
  vehicleSpeed: number;   // base speed m/s
  pedestrianSpeed: number;
  timeScale: number;      // simulation speed
  servicesEnabled: boolean;
  shadows: boolean;
  weather: WeatherType;
  following: boolean;
  useFlyControls: boolean;
  buildingStyle: 'retro' | 'modern';
  enablePoliceStation: boolean;
}

// Random helper with seed
class RNG {
  private s: number;
  constructor(seed: number) { this.s = seed >>> 0; }
  next(): number {
    // xorshift32
    let x = this.s;
    x ^= x << 13; x ^= x >>> 17; x ^= x << 5;
    this.s = x >>> 0;
    return (this.s % 0xFFFFFF) / 0xFFFFFF;
  }
  range(min: number, max: number) { return min + (max - min) * this.next(); }
  int(min: number, max: number) { return Math.floor(this.range(min, max + 1)); }
  pick<T>(arr: T[]) { return arr[Math.floor(this.next() * arr.length)]; }
}

// Globals
const container = document.getElementById('canvas-container') as HTMLDivElement;

const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
renderer.setSize(container.clientWidth, container.clientHeight);
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.shadowMap.autoUpdate = true;
renderer.shadowMap.needsUpdate = true;
container.appendChild(renderer.domElement);

const scene = new THREE.Scene();
scene.fog = new THREE.FogExp2(0x0b0e13, 0.0005);

const camera = new THREE.PerspectiveCamera(60, container.clientWidth / container.clientHeight, 0.1, 20000);
camera.position.set(250, 220, 300);
camera.lookAt(0, 0, 0);

const orbit = new OrbitControls(camera, renderer.domElement);
orbit.target.set(0, 0, 0);
orbit.enableDamping = false; // Remove damping for immediate stop
orbit.enablePan = true;
orbit.mouseButtons.RIGHT = THREE.MOUSE.PAN;
orbit.dampingFactor = 0.05; // Very low damping if needed

const fly = new FlyControls(camera, renderer.domElement);
fly.enabled = false;
fly.movementSpeed = 250;
fly.rollSpeed = Math.PI / 12;
fly.dragToLook = true;

// Stats
const stats = new Stats();
stats.showPanel(0);
Object.assign(stats.dom.style, { position: 'absolute', top: '52px', left: '8px', zIndex: '12' });
document.body.appendChild(stats.dom);

// GUI
const gui = new GUI();
gui.title('City Controls');
gui.domElement.style.top = '60px'; // Move below the timebar
gui.domElement.style.right = '12px';

const defaultConfig: CityConfig = {
  seed: 2,
  gridBlocks: 5,
  blockSize: 80,
  roadWidth: 20,
  sidewalkWidth: 6,
  carCount: 20, // Reduced for performance
  pedestrianCount: 140,
  petCount: 40,
  taxiFraction: 0.12,
  policeFraction: 0.06,
  ambulanceFraction: 0.05,
  deliveryFraction: 0.10,
  vehicleSpeed: 18,
  pedestrianSpeed: 1.4,
  timeScale: 1.0, // Normal time progression
  servicesEnabled: true,
  shadows: false,
  weather: 'Clear',
  following: false,
  useFlyControls: false,
  buildingStyle: 'retro',
  enablePoliceStation: true,
};

let CFG: CityConfig = { ...defaultConfig };

// Lighting and Sky
const hemiLight = new THREE.HemisphereLight(0xbad6ff, 0x223344, 0.6);
scene.add(hemiLight);

const sun = new THREE.DirectionalLight(0xfff1c1, 1.1);
sun.position.set(200, 400, 200);
sun.castShadow = true;
sun.shadow.mapSize.set(2048, 2048);
sun.shadow.camera.near = 10;
sun.shadow.camera.far = 1500;
sun.shadow.camera.left = -800;
sun.shadow.camera.right = 800;
sun.shadow.camera.top = 800;
sun.shadow.camera.bottom = -800;
scene.add(sun);

const skyGeo = new THREE.SphereGeometry(5000, 16, 16);
const skyMat = new THREE.ShaderMaterial({
  side: THREE.BackSide,
  uniforms: {
    topColor: { value: new THREE.Color(0x0a1930) },
    bottomColor: { value: new THREE.Color(0x0b0e13) },
    offset: { value: 0.5 },
    exponent: { value: 0.6 },
    mix: { value: 0.0 }
  },
  vertexShader: `
    varying vec3 vWorldPosition;
    void main(){
      vec4 p = modelMatrix * vec4(position,1.0);
      vWorldPosition = p.xyz;
      gl_Position = projectionMatrix * viewMatrix * p;
    }`,
  fragmentShader: `
    uniform vec3 topColor;
    uniform vec3 bottomColor;
    uniform float offset;
    uniform float exponent;
    uniform float mix;
    varying vec3 vWorldPosition;
    void main(){
      float h = normalize(vWorldPosition).y;
      vec3 c1 = mix(bottomColor, topColor, max(pow(max(h+offset,0.0), exponent), 0.0));
      vec3 c2 = vec3(0.02,0.02,0.03);
      gl_FragColor = vec4(mix(c1, c2, mix), 1.0);
    }`
});
const sky = new THREE.Mesh(skyGeo, skyMat);
scene.add(sky);

// Ground
const ground = new THREE.Mesh(
  new THREE.PlaneGeometry(10000, 10000),
  new THREE.MeshStandardMaterial({
    color: 0x0f141c,
    roughness: 1,
    metalness: 0,
    polygonOffset: true,
    polygonOffsetFactor: 1.0,
    polygonOffsetUnits: 1.0
  })
);
ground.rotation.x = -Math.PI / 2;
ground.position.y = -0.05; // Move ground slightly downward to prevent z-fighting
ground.receiveShadow = true;
scene.add(ground);

// Road textures
function makeRoadMaterial(): THREE.MeshStandardMaterial {
  const tex = document.createElement('canvas');
  tex.width = 256; tex.height = 256;
  const ctx = tex.getContext('2d')!;
  ctx.fillStyle = '#2b2f36';
  ctx.fillRect(0, 0, 256, 256);
  ctx.strokeStyle = '#dcdcdc';
  ctx.lineWidth = 4;
  // dashed lane markers horizontally and vertically
  const dash = (x1: number, y1: number, x2: number, y2: number, seg=10) => {
    const dx = (x2 - x1) / seg;
    const dy = (y2 - y1) / seg;
    for (let i=0;i<seg;i+=2){
      ctx.beginPath();
      ctx.moveTo(x1 + dx*i, y1 + dy*i);
      ctx.lineTo(x1 + dx*(i+1), y1 + dy*(i+1));
      ctx.stroke();
    }
  };
  dash(128, 0, 128, 256);
  dash(0, 128, 256, 128);
  const t = new THREE.CanvasTexture(tex);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(8, 8);
  return new THREE.MeshStandardMaterial({ map: t, roughness: 1, metalness: 0 });
}

const roadMaterial = makeRoadMaterial();

// City graph (roads, blocks)
interface Intersection {
  i: number; j: number;
  pos: THREE.Vector3;
}

interface Lane {
  // start -> end on a road with given direction
  from: Intersection;
  to: Intersection;
  dir: 'x' | 'z';
  y: number;
  xzStart: number;
  xzEnd: number;
  worldStart: THREE.Vector3;
  worldEnd: THREE.Vector3;
}

const City = {
  rng: new RNG(CFG.seed),
  intersections: [] as Intersection[],
  lanes: [] as Lane[],
  blocks: [] as THREE.Box3[],
  shops: new Set<number>(), // block index that is shop
  parks: new Set<number>(), // block index that is park
  root: new THREE.Group(),
};
scene.add(City.root);

// Entities
type VehicleType = 'car' | 'taxi' | 'police' | 'ambulance' | 'delivery';
type PersonType = 'adult' | 'kid';

let ENTITIES: {
  vehicles: Vehicle[];
  people: Person[];
  pets: Pet[];
  buildings: THREE.Object3D[];
  parks: THREE.Object3D[];
  rain?: THREE.Points;
  selected?: THREE.Object3D | null;
} = {
  vehicles: [],
  people: [],
  pets: [],
  buildings: [],
  parks: [],
  selected: null
};

// Sierra Police Quest Retro Materials
function createBrickMaterial(color: number = 0x8B4513): THREE.MeshStandardMaterial {
  const canvas = document.createElement('canvas');
  canvas.width = 64; canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  
  // Base brick color
  ctx.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
  ctx.fillRect(0, 0, 64, 64);
  
  // Mortar lines
  ctx.fillStyle = '#F5F5DC';
  for (let y = 0; y < 64; y += 16) {
    for (let x = 0; x < 64; x += 32) {
      const offset = (y / 16) % 2 === 0 ? 0 : 16;
      ctx.fillRect(x + offset, y, 2, 16);
      ctx.fillRect(x + offset + 14, y, 2, 16);
    }
  }
  
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(4, 4);
  
  return new THREE.MeshStandardMaterial({
    map: texture,
    roughness: 0.8,
    metalness: 0.1
  });
}

function createConcreteMaterial(): THREE.MeshStandardMaterial {
  const canvas = document.createElement('canvas');
  canvas.width = 64; canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  
  // Base concrete color
  ctx.fillStyle = '#D3D3D3';
  ctx.fillRect(0, 0, 64, 64);
  
  // Texture variations
  ctx.fillStyle = '#A9A9A9';
  for (let i = 0; i < 100; i++) {
    const x = Math.random() * 64;
    const y = Math.random() * 64;
    const size = Math.random() * 2;
    ctx.fillRect(x, y, size, size);
  }
  
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(8, 8);
  
  return new THREE.MeshStandardMaterial({
    map: texture,
    roughness: 0.9,
    metalness: 0.05
  });
}

function createStuccoMaterial(): THREE.MeshStandardMaterial {
  const canvas = document.createElement('canvas');
  canvas.width = 64; canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  
  // Base stucco color
  ctx.fillStyle = '#F5F5DC';
  ctx.fillRect(0, 0, 64, 64);
  
  // Stucco texture
  ctx.fillStyle = '#E6E6FA';
  for (let i = 0; i < 200; i++) {
    const x = Math.random() * 64;
    const y = Math.random() * 64;
    const size = Math.random() * 3;
    ctx.fillRect(x, y, size, size);
  }
  
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(6, 6);
  
  return new THREE.MeshStandardMaterial({
    map: texture,
    roughness: 0.7,
    metalness: 0.1
  });
}

// Retro Materials
const Materials = {
  asphalt: roadMaterial,
  grass: new THREE.MeshStandardMaterial({ color: 0x214d2f, roughness: 1 }),
  concrete: createConcreteMaterial(),
  stucco: createStuccoMaterial(),
  
  // Retro building materials
  brickReddish: createBrickMaterial(0x8B4513), // Reddish-brown brick
  brickDark: createBrickMaterial(0x654321),    // Dark brick
  brickLight: createBrickMaterial(0xA0522D),   // Light brick
  
  // Vehicle colors (Police Quest style)
  carColors: [0x4169E1, 0xFFFFFF, 0xDC143C, 0x000000, 0x8B4513, 0x32CD32].map(c => new THREE.Color(c)), // Royal blue, white, crimson, black, saddle brown, lime green
  policeBlack: new THREE.Color(0x000000),
  policeWhite: new THREE.Color(0xFFFFFF),
  taxiYellow: new THREE.Color(0xFFD700),
  ambulanceWhite: new THREE.Color(0xFFFFFF),
  ambulanceRed: new THREE.Color(0xFF0000),
  deliveryBlue: new THREE.Color(0x4169E1),
  
  // Retro glass
  glass: new THREE.MeshStandardMaterial({
    color: 0x87CEEB,
    metalness: 0.1,
    roughness: 0.1,
    envMapIntensity: 0.3,
    transparent: true,
    opacity: 0.7
  }),
  
  person: new THREE.Color(0x6adea8),
  pet: new THREE.Color(0xe0aaff),
  
  // Police Quest specific colors
  policeBlue: new THREE.Color(0x0000FF),
  policeLightRed: new THREE.Color(0xFF0000),
  policeLightBlue: new THREE.Color(0x0000FF),
};

// Sierra Police Quest Style Building Builders

function buildPoliceStation(w: number, d: number, h: number): THREE.Group {
  const station = new THREE.Group();
  
  // Main building structure - reddish-brown brick facade
  const mainGeo = new THREE.BoxGeometry(w, h, d);
  const mainMat = Materials.brickReddish;
  const mainMesh = new THREE.Mesh(mainGeo, mainMat);
  mainMesh.castShadow = CFG.shadows;
  mainMesh.receiveShadow = true;
  station.add(mainMesh);

  // Add large arched entrance with blue/white striped awning
  const entranceWidth = w * 0.4;
  const entranceHeight = h * 0.3;
  const entranceDepth = 0.3;
  
  // Arched entrance
  const archGeo = new THREE.CylinderGeometry(entranceWidth/2, entranceWidth/2, entranceDepth, 16);
  const archMat = new THREE.MeshStandardMaterial({ color: 0x2F4F4F });
  const arch = new THREE.Mesh(archGeo, archMat);
  arch.rotation.z = Math.PI / 2;
  arch.position.set(0, -h/2 + entranceHeight/2, d/2 + entranceDepth/2);
  station.add(arch);

  // Blue/white striped awning
  const awningGeo = new THREE.BoxGeometry(entranceWidth + 2, 0.3, 1);
  const awningCanvas = document.createElement('canvas');
  awningCanvas.width = 64; awningCanvas.height = 32;
  const awningCtx = awningCanvas.getContext('2d')!;
  
  // Create striped pattern
  for (let x = 0; x < 64; x += 8) {
    awningCtx.fillStyle = x % 16 === 0 ? '#0000FF' : '#FFFFFF';
    awningCtx.fillRect(x, 0, 8, 32);
  }
  
  const awningTex = new THREE.CanvasTexture(awningCanvas);
  const awningMat = new THREE.MeshStandardMaterial({ map: awningTex });
  const awning = new THREE.Mesh(awningGeo, awningMat);
  awning.position.set(0, -h/2 + entranceHeight + 0.2, d/2 + 0.6);
  station.add(awning);

  // Police sign
  const signGeo = new THREE.PlaneGeometry(w * 0.8, 0.3);
  const signMat = new THREE.MeshStandardMaterial({
    color: 0x808080,
    emissive: 0xFFFFFF,
    emissiveIntensity: 0.1
  });
  const sign = new THREE.Mesh(signGeo, signMat);
  sign.position.set(0, h/2 - 1, d/2 + 0.1);
  
  // Add sign text using sprite
  const signText = new THREE.Sprite(new THREE.SpriteMaterial({ color: 0xFFFFFF }));
  signText.scale.set(w * 0.6, 0.2, 1);
  signText.position.set(0, 0, 0.01);
  sign.add(signText);
  station.add(sign);

  // Windows with white frames
  const windowWidth = 3;
  const windowHeight = 4;
  const windowSpacing = 6;
  
  for (let y = h/2 - 3; y > -h/2 + 3; y -= windowSpacing) {
    for (let x = -w/2 + 4; x < w/2 - 4; x += windowSpacing) {
      // Window frame
      const frameGeo = new THREE.BoxGeometry(windowWidth + 0.4, windowHeight + 0.4, 0.2);
      const frameMat = new THREE.MeshStandardMaterial({ color: 0xFFFFFF });
      const frame = new THREE.Mesh(frameGeo, frameMat);
      frame.position.set(x, y, d/2 + 0.1);
      station.add(frame);
      
      // Window glass
      const glassGeo = new THREE.BoxGeometry(windowWidth, windowHeight, 0.1);
      const glassMat = Materials.glass;
      const glass = new THREE.Mesh(glassGeo, glassMat);
      glass.position.set(x, y, d/2 + 0.15);
      station.add(glass);
    }
  }

  // Flat roof with HVAC units
  const roofHeight = 0.5;
  const roofGeo = new THREE.BoxGeometry(w + 1, roofHeight, d + 1);
  const roofMat = createConcreteMaterial();
  const roof = new THREE.Mesh(roofGeo, roofMat);
  roof.position.set(0, h/2 + roofHeight/2, 0);
  station.add(roof);

  // HVAC units
  const hvacGeo = new THREE.BoxGeometry(2, 1, 3);
  const hvacMat = new THREE.MeshStandardMaterial({ color: 0x696969 });
  const hvac1 = new THREE.Mesh(hvacGeo, hvacMat);
  hvac1.position.set(-w/4, h/2 + roofHeight/2 + 0.5, 0);
  station.add(hvac1);

  const hvac2 = new THREE.Mesh(hvacGeo, hvacMat);
  hvac2.position.set(w/4, h/2 + roofHeight/2 + 0.5, 0);
  station.add(hvac2);

  // Satellite dish
  const dishGeo = new THREE.ConeGeometry(0.5, 0.2, 8);
  const dishMat = new THREE.MeshStandardMaterial({ color: 0xC0C0C0 });
  const dish = new THREE.Mesh(dishGeo, dishMat);
  dish.rotation.x = Math.PI / 2;
  dish.position.set(0, h/2 + roofHeight/2 + 1, 0);
  station.add(dish);

  // Flagpole with American flag
  const poleGeo = new THREE.CylinderGeometry(0.1, 0.1, 8);
  const poleMat = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
  const pole = new THREE.Mesh(poleGeo, poleMat);
  pole.position.set(w/2 - 1, h/2 + 4, 0);
  station.add(pole);

  // Flag (simplified as a plane)
  const flagGeo = new THREE.PlaneGeometry(2, 1.5);
  const flagMat = new THREE.MeshStandardMaterial({
    color: 0xFF0000,
    side: THREE.DoubleSide
  });
  const flag = new THREE.Mesh(flagGeo, flagMat);
  flag.position.set(w/2 - 1, h/2 + 4.5, 0.01);
  station.add(flag);

  // Chain-link fence
  const fenceHeight = 2;
  const fencePosts: THREE.Mesh[] = [];
  const fenceSegments: THREE.Mesh[] = [];
  
  for (let x = -w/2 - 5; x <= w/2 + 5; x += 2) {
    // Fence posts
    const postGeo = new THREE.BoxGeometry(0.2, fenceHeight, 0.2);
    const postMat = new THREE.MeshStandardMaterial({ color: 0xC0C0C0 });
    const post = new THREE.Mesh(postGeo, postMat);
    post.position.set(x, -h/2 + fenceHeight/2, d/2 + 1);
    station.add(post);
    fencePosts.push(post);
  }

  return station;
}

function buildCommercialBuilding(w: number, d: number, h: number, shopName: string = 'SHOP'): THREE.Group {
  const building = new THREE.Group();
  
  // Main structure - brick base with stucco upper floors
  const mainGeo = new THREE.BoxGeometry(w, h, d);
  const mainMat = Materials.brickDark;
  const mainMesh = new THREE.Mesh(mainGeo, mainMat);
  mainMesh.castShadow = CFG.shadows;
  mainMesh.receiveShadow = true;
  building.add(mainMesh);

  // Stucco upper floors
  const upperHeight = h * 0.6;
  const upperGeo = new THREE.BoxGeometry(w - 0.2, upperHeight, d - 0.2);
  const upperMat = Materials.stucco;
  const upperMesh = new THREE.Mesh(upperGeo, upperMat);
  upperMesh.position.set(0, h/2 - upperHeight/2, 0);
  building.add(upperMesh);

  // Large storefront windows
  const storefrontWidth = w * 0.8;
  const storefrontHeight = h * 0.4;
  const storefrontGeo = new THREE.BoxGeometry(storefrontWidth, storefrontHeight, 0.1);
  const storefrontMat = Materials.glass;
  const storefront = new THREE.Mesh(storefrontGeo, storefrontMat);
  storefront.position.set(0, -h/2 + storefrontHeight/2, d/2 + 0.05);
  building.add(storefront);

  // Storefront awning
  const awningGeo = new THREE.BoxGeometry(storefrontWidth + 1, 0.2, 0.8);
  const awningMat = new THREE.MeshStandardMaterial({ color: 0xFF0000 });
  const awning = new THREE.Mesh(awningGeo, awningMat);
  awning.position.set(0, -h/2 + storefrontHeight + 0.1, d/2 + 0.4);
  building.add(awning);

  // Store sign
  const signGeo = new THREE.PlaneGeometry(w * 0.6, 0.2);
  const signMat = new THREE.MeshStandardMaterial({
    color: 0xFFFFFF,
    emissive: 0xFFFFFF,
    emissiveIntensity: 0.2
  });
  const sign = new THREE.Mesh(signGeo, signMat);
  sign.position.set(0, -h/2 + storefrontHeight + 0.5, d/2 + 0.1);
  building.add(sign);

  // Regular windows on upper floors
  const windowWidth = 2;
  const windowHeight = 3;
  const windowSpacing = 4;
  
  for (let y = h/2 - 2; y > -h/2 + storefrontHeight + 2; y -= windowSpacing) {
    for (let x = -w/2 + 3; x < w/2 - 3; x += windowSpacing) {
      const windowGeo = new THREE.BoxGeometry(windowWidth, windowHeight, 0.1);
      const windowMat = Materials.glass;
      const window = new THREE.Mesh(windowGeo, windowMat);
      window.position.set(x, y, d/2 + 0.05);
      building.add(window);
    }
  }

  // Gabled roof
  const roofHeight = h * 0.2;
  const roofGeo = new THREE.BoxGeometry(w + 1, roofHeight, d + 1);
  const roofMat = new THREE.MeshStandardMaterial({
    color: 0x8B4513,
    roughness: 0.9,
    metalness: 0.1
  });
  const roof = new THREE.Mesh(roofGeo, roofMat);
  roof.position.set(0, h/2 + roofHeight/2, 0);
  building.add(roof);

  return building;
}

function buildResidentialBuilding(w: number, d: number, h: number): THREE.Group {
  const house = new THREE.Group();
  
  // Main structure - mix of siding and brick
  const mainGeo = new THREE.BoxGeometry(w, h * 0.7, d);
  const mainMat = Materials.stucco;
  const mainMesh = new THREE.Mesh(mainGeo, mainMat);
  mainMesh.castShadow = CFG.shadows;
  mainMesh.receiveShadow = true;
  house.add(mainMesh);

  // Brick foundation
  const foundationHeight = h * 0.1;
  const foundationGeo = new THREE.BoxGeometry(w + 0.4, foundationHeight, d + 0.4);
  const foundationMat = Materials.brickLight;
  const foundation = new THREE.Mesh(foundationGeo, foundationMat);
  foundation.position.set(0, -h/2 + foundationHeight/2, 0);
  house.add(foundation);

  // Garage
  const garageWidth = w * 0.4;
  const garageDepth = d * 0.6;
  const garageHeight = h * 0.6;
  const garageGeo = new THREE.BoxGeometry(garageWidth, garageHeight, garageDepth);
  const garageMat = Materials.brickDark;
  const garage = new THREE.Mesh(garageGeo, garageMat);
  garage.position.set(w/2 - garageWidth/2, -h/2 + garageHeight/2, 0);
  house.add(garage);

  // Garage door
  const doorGeo = new THREE.PlaneGeometry(garageWidth - 0.2, garageHeight - 0.2);
  const doorMat = new THREE.MeshStandardMaterial({ color: 0x4169E1 });
  const garageDoor = new THREE.Mesh(doorGeo, doorMat);
  garageDoor.position.set(w/2 - garageWidth/2, -h/2 + garageHeight/2, garageDepth/2 + 0.01);
  house.add(garageDoor);

  // Front door
  const frontDoorWidth = 2;
  const frontDoorHeight = h * 0.25;
  const frontDoorGeo = new THREE.PlaneGeometry(frontDoorWidth, frontDoorHeight);
  const frontDoorMat = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
  const frontDoor = new THREE.Mesh(frontDoorGeo, frontDoorMat);
  frontDoor.position.set(0, -h/2 + frontDoorHeight/2, d/2 + 0.01);
  house.add(frontDoor);

  // Windows
  const windowWidth = 1.5;
  const windowHeight = 2;
  
  // Front windows
  const leftWindowGeo = new THREE.BoxGeometry(windowWidth, windowHeight, 0.1);
  const leftWindowMat = Materials.glass;
  const leftWindow = new THREE.Mesh(leftWindowGeo, leftWindowMat);
  leftWindow.position.set(-w/4, -h/2 + windowHeight/2 + 1, d/2 + 0.05);
  house.add(leftWindow);

  const rightWindowGeo = new THREE.BoxGeometry(windowWidth, windowHeight, 0.1);
  const rightWindow = new THREE.Mesh(rightWindowGeo, leftWindowMat);
  rightWindow.position.set(w/4, -h/2 + windowHeight/2 + 1, d/2 + 0.05);
  house.add(rightWindow);

  // Gabled roof with asphalt shingles
  const roofHeight = h * 0.3;
  const roofGeo = new THREE.BoxGeometry(w + 0.6, roofHeight, d + 0.6);
  const roofMat = new THREE.MeshStandardMaterial({
    color: 0x2F4F4F,
    roughness: 0.8,
    metalness: 0.2
  });
  const roof = new THREE.Mesh(roofGeo, roofMat);
  roof.position.set(0, h/2 + roofHeight/2, 0);
  house.add(roof);

  // Chimney
  const chimneyGeo = new THREE.BoxGeometry(1, h * 0.4, 1);
  const chimneyMat = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
  const chimney = new THREE.Mesh(chimneyGeo, chimneyMat);
  chimney.position.set(w/4, h/2 + roofHeight/2 + h * 0.2, -d/4);
  house.add(chimney);

  return house;
}

// Mesh builders
function buildBuilding(w: number, d: number, h: number, color: number, windows: boolean, shop=false, buildingType: BuildingType = 'residential'): THREE.Group {
  switch (buildingType) {
    case 'police':
      return buildPoliceStation(w, d, h);
    case 'commercial':
      return buildCommercialBuilding(w, d, h, shop ? 'SHOP' : 'STORE');
    case 'residential':
    default:
      return buildResidentialBuilding(w, d, h);
  }
}
function makeImprovedWindowCanvas(buildingWidth: number, buildingHeight: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  const scale = Math.max(1, Math.floor(buildingWidth / 8));
  c.width = 128 * scale;
  c.height = 128 * scale;
  const ctx = c.getContext('2d')!;
  
  // Background
  ctx.fillStyle = '#1a2332';
  ctx.fillRect(0, 0, c.width, c.height);
  
  // Window frames
  ctx.fillStyle = '#2a3441';
  const windowWidth = 12 * scale;
  const windowHeight = 16 * scale;
  const spacing = 8 * scale;
  
  for (let y = spacing; y < c.height - windowHeight; y += windowHeight + spacing) {
    for (let x = spacing; x < c.width - windowWidth; x += windowWidth + spacing) {
      // Window frame
      ctx.fillRect(x, y, windowWidth, windowHeight);
      
      // Window glass with subtle variation
      const glassColor = Math.random() < 0.7 ? '#4a6fa5' : '#6b8cb8';
      ctx.fillStyle = glassColor;
      ctx.fillRect(x + 2, y + 2, windowWidth - 4, windowHeight - 4);
      
      // Window reflection
      ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.fillRect(x + 2, y + 2, windowWidth - 8, 4);
    }
  }
  
  return c;
}

function makeWindowCanvas(): HTMLCanvasElement {
  const c = document.createElement('canvas'); c.width=64; c.height=64;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#2c3644'; ctx.fillRect(0,0,64,64);
  for (let y=4;y<64;y+=8){
    for (let x=4;x<64;x+=8){
      ctx.fillStyle = Math.random() < 0.5 ? '#38475e' : '#253243';
      ctx.fillRect(x, y, 4, 4);
    }
  }
  return c;
}

function makeShopSign(): THREE.Object3D {
  const g = new THREE.Group();
  const plate = new THREE.Mesh(
    new THREE.PlaneGeometry(22, 5),
    new THREE.MeshStandardMaterial({ color: 0xc5ff6a, emissive: 0x304a20, emissiveIntensity: 0.2 })
  );
  const text = new THREE.Sprite(new THREE.SpriteMaterial({ color: 0x1a2a10 }));
  text.scale.set(10, 2, 1);
  text.position.set(0, 0, 0.1);
  g.add(plate, text);
  return g;
}

function buildTree(): THREE.Object3D {
  const trunk = new THREE.Mesh(
    new THREE.CylinderGeometry(0.6, 0.8, 4, 6),
    new THREE.MeshStandardMaterial({ color: 0x6b4f3b, roughness: 1 })
  );
  trunk.castShadow = CFG.shadows;
  const crown = new THREE.Mesh(
    new THREE.SphereGeometry(3, 10, 10),
    new THREE.MeshStandardMaterial({ color: 0x2e8b57, roughness: 1 })
  );
  crown.position.y = 3;
  crown.castShadow = CFG.shadows;
  const g = new THREE.Group();
  g.add(trunk, crown);
  return g;
}

function buildCar(type: VehicleType, color?: THREE.Color): THREE.Object3D {
  const bodyColor =
    type === 'taxi' ? Materials.taxiYellow :
    type === 'police' ? Materials.policeWhite :
    type === 'ambulance' ? Materials.ambulanceWhite :
    type === 'delivery' ? Materials.deliveryBlue :
    color ?? Materials.carColors[Math.floor(Math.random()*Materials.carColors.length)];

  const bodyMat = new THREE.MeshStandardMaterial({ color: bodyColor, metalness: 0.2, roughness: 0.5 });
  const cabinMat = new THREE.MeshStandardMaterial({ color: 0xeeeeee, roughness: 0.2, metalness: 0.1 });
  const wheelMat = new THREE.MeshStandardMaterial({ color: 0x222222, roughness: 1 });

  const car = new THREE.Group();

  const body = new THREE.Mesh(new THREE.BoxGeometry(3, 1, 6), bodyMat);
  body.castShadow = CFG.shadows;
  const cabin = new THREE.Mesh(new THREE.BoxGeometry(2.4, 1.2, 2.8), cabinMat);
  cabin.position.set(0, 1.1, -0.2);
  cabin.castShadow = CFG.shadows;

  const wheels: THREE.Mesh[] = [];
  const wheelGeo = new THREE.CylinderGeometry(0.6, 0.6, 0.6, 16);
  for (const [x,z] of [[1.4, 2.0], [-1.4, 2.0], [1.4, -2.0], [-1.4, -2.0]]) {
    const w = new THREE.Mesh(wheelGeo, wheelMat);
    w.rotation.z = Math.PI/2;
    w.position.set(x, -0.4, z);
    w.castShadow = CFG.shadows;
    wheels.push(w);
  }

  car.add(body, cabin, ...wheels);

  if (type === 'police' || type === 'ambulance') {
    const lightLeft = new THREE.Mesh(new THREE.BoxGeometry(0.4,0.3,0.4), new THREE.MeshStandardMaterial({ color: 0x66ccff, emissive: 0x66ccff, emissiveIntensity: 0.8 }));
    const lightRight = new THREE.Mesh(new THREE.BoxGeometry(0.4,0.3,0.4), new THREE.MeshStandardMaterial({ color: 0xff6688, emissive: 0xff6688, emissiveIntensity: 0.8 }));
    lightLeft.position.set(-0.6, 1.2, -0.6);
    lightRight.position.set(0.6, 1.2, -0.6);
    car.add(lightLeft, lightRight);
    (car.userData as any).sirenLights = [lightLeft, lightRight];
  }

  return car;
}

function buildPerson(): THREE.Object3D {
  const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.35, 1.2, 6, 10), new THREE.MeshStandardMaterial({ color: Materials.person }));
  body.castShadow = CFG.shadows;
  return body;
}

function buildPet(): THREE.Object3D {
  const dog = new THREE.Group();
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.3, 8, 8), new THREE.MeshStandardMaterial({ color: Materials.pet }));
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.2, 8, 8), new THREE.MeshStandardMaterial({ color: Materials.pet }));
  head.position.set(0.35, 0.05, 0);
  const tail = new THREE.Mesh(new THREE.ConeGeometry(0.08, 0.25, 6), new THREE.MeshStandardMaterial({ color: Materials.pet }));
  tail.position.set(-0.35, 0.05, 0);
  tail.rotation.z = Math.PI/2;
  dog.add(body, head, tail);
  dog.castShadow = CFG.shadows;
  return dog;
}

// Vehicles / People classes
class Vehicle {
  obj: THREE.Object3D;
  type: VehicleType;
  speed: number;
  lane: Lane;
  t: number; // progress along lane 0..1
  targetLane: Lane | null = null;
  blink: number = 0;
  waitTimer: number = 0;
  constructor(type: VehicleType, lane: Lane, color?: THREE.Color) {
    this.obj = buildCar(type, color);
    this.type = type;
    this.lane = lane;
    this.t = Math.random();
    this.speed = CFG.vehicleSpeed * (0.7 + Math.random()*0.6); // vary speed
    this.obj.position.copy(this.positionOnLane());
    // Fix car rotation: cars should face along the road direction
    this.obj.rotation.y = lane.dir === 'x' ? Math.PI/2 : 0;
    City.root.add(this.obj);
  }
  positionOnLane(): THREE.Vector3 {
    const v = new THREE.Vector3().lerpVectors(this.lane.worldStart, this.lane.worldEnd, this.t);
    v.y = 0.5;
    return v;
  }
  update(dt: number) {
    // siren flash
    this.blink += dt;
    if ((this.type === 'police' || this.type === 'ambulance') && (this.obj.userData as any).sirenLights) {
      const [L, R] = (this.obj.userData as any).sirenLights as THREE.Mesh[];
      const phase = Math.sin(this.blink * 10);
      (L.material as any).emissiveIntensity = Math.max(0, phase);
      (R.material as any).emissiveIntensity = Math.max(0, -phase);
    }

    // waiting (e.g., delivery at shop)
    if (this.waitTimer > 0) {
      this.waitTimer -= dt;
      return;
    }

    // basic progress + collision avoidance
    let advance = (this.speed * dt) / laneLength(this.lane);

    // check for a close vehicle ahead (very simple)
    const ahead = findVehicleAhead(this, 6);
    if (ahead) advance *= 0.3;

    this.t += advance;
    if (this.t >= 1.0) {
      // choose next lane
      const next = nextLaneFromIntersection(this.lane.to, this.lane.dir);
      this.lane = next;
      this.t = 0;
      this.obj.rotation.y = this.lane.dir === 'x' ? Math.PI/2 : 0;

      // chance of service behavior
      if (CFG.servicesEnabled) {
        if (this.type === 'delivery' && Math.random() < 0.35) {
          // if near a shop, stop briefly
          const shop = nearestShop(this.obj.position);
          if (shop && shop.distance < 20) this.waitTimer = 2 + Math.random()*4;
        }
      }
    }
    this.obj.position.copy(this.positionOnLane());
  }
}

class Person {
  obj: THREE.Object3D;
  speed: number;
  path: THREE.Vector3[] = [];
  idx = 0;
  pause = 0;
  constructor() {
    this.obj = buildPerson();
    this.speed = CFG.pedestrianSpeed * (0.7 + Math.random()*0.6);
    City.root.add(this.obj);
    this.newPath();
  }
  newPath() {
    this.path = makeSidewalkPath();
    this.idx = 0;
    if (this.path.length > 0) this.obj.position.copy(this.path[0]).setY(0.0);
  }
  update(dt: number) {
    if (this.pause > 0) { this.pause -= dt; return; }
    if (this.path.length === 0) return;
    const target = this.path[this.idx];
    const pos = this.obj.position;
    const dir = new THREE.Vector3().subVectors(target, pos); dir.y = 0;
    const dist = dir.length();
    if (dist < 0.5) {
      this.idx++;
      if (this.idx >= this.path.length) {
        this.pause = Math.random()*3;
        this.newPath();
      }
      return;
    }
    dir.normalize();
    pos.addScaledVector(dir, this.speed * dt);
    const yaw = Math.atan2(dir.x, dir.z);
    this.obj.rotation.y = yaw;
  }
}

class Pet {
  obj: THREE.Object3D;
  master: Person | null = null;
  angle = Math.random()*Math.PI*2;
  radius = 1.5 + Math.random()*1.5;
  constructor(master?: Person) {
    this.obj = buildPet();
    City.root.add(this.obj);
    if (master) {
      this.master = master;
      this.obj.position.copy(master.obj.position).add(new THREE.Vector3(Math.random(), 0, Math.random()));
    } else {
      this.obj.position.set((Math.random()-0.5)*50, 0, (Math.random()-0.5)*50);
    }
  }
  update(dt: number) {
    if (this.master) {
      this.angle += dt * 1.5;
      const center = this.master.obj.position;
      this.obj.position.set(center.x + Math.cos(this.angle)*this.radius, 0, center.z + Math.sin(this.angle)*this.radius);
      this.obj.rotation.y = this.angle + Math.PI/2;
    }
  }
}

// Build city grid and content
function laneLength(l: Lane) {
  return l.worldStart.distanceTo(l.worldEnd);
}

function buildCity() {
  // reset root
  for (const child of [...City.root.children]) City.root.remove(child);
  ENTITIES.vehicles.forEach(v => v.obj.removeFromParent());
  ENTITIES.people.forEach(p => p.obj.removeFromParent());
  ENTITIES.pets.forEach(p => p.obj.removeFromParent());
  ENTITIES.buildings.forEach(b => b.removeFromParent());
  ENTITIES.parks.forEach(p => p.removeFromParent());
  ENTITIES = { ...ENTITIES, vehicles: [], people: [], pets: [], buildings: [], parks: [], rain: ENTITIES.rain, selected: null };

  // ground remains attached to scene, but not in City.root

  City.rng = new RNG(CFG.seed);
  City.intersections = [];
  City.lanes = [];
  City.blocks = [];
  City.shops.clear();
  City.parks.clear();

  const N = CFG.gridBlocks;
  const B = CFG.blockSize;
  const R = CFG.roadWidth;
  const S = CFG.sidewalkWidth;

  const citySize = N * B + (N + 1) * R;
  const start = -citySize / 2;

  // Roads (as a grid of strips)
  const roadsGroup = new THREE.Group();
  // horizontal roads (x axis)
  for (let j=0; j<=N; j++) {
    const z = start + j*(B + R);
    const road = new THREE.Mesh(new THREE.BoxGeometry(citySize + R, 0.05, R), Materials.asphalt);
    road.position.set(0, 0.01, z);
    road.receiveShadow = true;
    roadsGroup.add(road);
  }
  // vertical roads (z axis)
  for (let i=0; i<=N; i++) {
    const x = start + i*(B + R);
    const road = new THREE.Mesh(new THREE.BoxGeometry(R, 0.05, citySize + R), Materials.asphalt);
    road.position.set(x, 0.01, 0);
    road.receiveShadow = true;
    roadsGroup.add(road);
  }
  City.root.add(roadsGroup);

  // Intersections
  for (let i=0;i<=N;i++) {
    for (let j=0;j<=N;j++) {
      const x = start + i*(B + R);
      const z = start + j*(B + R);
      City.intersections.push({ i, j, pos: new THREE.Vector3(x, 0, z) });
    }
  }

  // Lanes along +x
  for (let j=0;j<=N;j++) {
    for (let i=0;i<N;i++) {
      const a = getIntersection(i, j), b = getIntersection(i+1, j);
      const offset = R * 0.25; // two lanes per road
      const y = 0.0;
      // eastbound lane
      City.lanes.push({
        from: a, to: b, dir: 'x', y,
        xzStart: a.pos.x + offset, xzEnd: b.pos.x - offset,
        worldStart: new THREE.Vector3(a.pos.x + offset, y, a.pos.z),
        worldEnd: new THREE.Vector3(b.pos.x - offset, y, b.pos.z),
      });
      // westbound lane
      City.lanes.push({
        from: b, to: a, dir: 'x', y,
        xzStart: b.pos.x - offset, xzEnd: a.pos.x + offset,
        worldStart: new THREE.Vector3(b.pos.x - offset, y, b.pos.z),
        worldEnd: new THREE.Vector3(a.pos.x + offset, y, a.pos.z),
      });
    }
  }
  // Lanes along +z
  for (let i=0;i<=N;i++) {
    for (let j=0;j<N;j++) {
      const a = getIntersection(i, j), b = getIntersection(i, j+1);
      const offset = R * 0.25;
      const y = 0.0;
      // southbound
      City.lanes.push({
        from: a, to: b, dir: 'z', y,
        xzStart: a.pos.z + offset, xzEnd: b.pos.z - offset,
        worldStart: new THREE.Vector3(a.pos.x, y, a.pos.z + offset),
        worldEnd: new THREE.Vector3(b.pos.x, y, b.pos.z - offset),
      });
      // northbound
      City.lanes.push({
        from: b, to: a, dir: 'z', y,
        xzStart: b.pos.z - offset, xzEnd: a.pos.z + offset,
        worldStart: new THREE.Vector3(b.pos.x, y, b.pos.z - offset),
        worldEnd: new THREE.Vector3(a.pos.x, y, a.pos.z + offset),
      });
    }
  }

  // Blocks, Buildings / Parks
  const buildingsGroup = new THREE.Group();
  const parksGroup = new THREE.Group();
  for (let i=0;i<N;i++) {
    for (let j=0;j<N;j++) {
      const x0 = start + i*(B + R) + R;
      const z0 = start + j*(B + R) + R;
      const cx = x0 + B/2;
      const cz = z0 + B/2;
      const blockBox = new THREE.Box3(
        new THREE.Vector3(x0 + S, 0, z0 + S),
        new THREE.Vector3(x0 + B - S, 0, z0 + B - S),
      );
      City.blocks.push(blockBox);

      // Park or building?
      const isPark = Math.random() < 0.2;
      const isShop = !isPark && Math.random() < 0.25;
      const idx = i*(N) + j;
      if (isPark) City.parks.add(idx);
      if (isShop) City.shops.add(idx);

      if (isPark) {
        const park = new THREE.Mesh(new THREE.BoxGeometry(B - 2*S, 0.02, B - 2*S), Materials.grass);
        park.position.set(cx, 0.02, cz);
        park.receiveShadow = true;
        parksGroup.add(park);
        ENTITIES.parks.push(park);
        // trees
        const trees = 8 + City.rng.int(0, 10);
        for (let t=0;t<trees;t++) {
          const tr = buildTree();
          tr.position.set(
            City.rng.range(x0 + S + 5, x0 + B - S - 5),
            0,
            City.rng.range(z0 + S + 5, z0 + B - S - 5)
          );
          parksGroup.add(tr);
        }
      } else {
        // cluster of buildings within block
        const count = 1 + City.rng.int(0, 3);
        for (let k=0;k<count;k++) {
          const w = City.rng.range(12, Math.min(28, B/2));
          const d = City.rng.range(12, Math.min(28, B/2));
          const h = City.rng.range(18, 90);
          
          // Determine building type based on configuration
          let buildingType: BuildingType = 'residential';
          if (CFG.enablePoliceStation && i === 0 && j === 0 && k === 0) {
            buildingType = 'police';
          } else if (isShop && k === 0) {
            buildingType = 'commercial';
          }
          
          const b = buildBuilding(w, d, h, 0xFFFFFF, true, isShop && k === 0, buildingType);
          b.position.set(
            City.rng.range(x0 + S + w/2, x0 + B - S - w/2),
            h/2 + 0.05,
            City.rng.range(z0 + S + d/2, z0 + B - S - d/2)
          );
          b.castShadow = CFG.shadows;
          buildingsGroup.add(b);
          ENTITIES.buildings.push(b);
        }
      }
    }
  }
  City.root.add(buildingsGroup);
  City.root.add(parksGroup);

  // Sidewalks simple outline
  const sidewalks = new THREE.Group();
  const sidewalkMat = new THREE.MeshStandardMaterial({ color: 0x4a5568, roughness: 0.9 });
  for (let i=0;i<N;i++) {
    for (let j=0;j<N;j++) {
      const x0 = start + i*(B + R) + R;
      const z0 = start + j*(B + R) + R;
      const geo = new THREE.BoxGeometry(B, 0.04, B);
      const sw = new THREE.Mesh(geo, sidewalkMat);
      sw.position.set(x0 + B/2, 0.02, z0 + B/2);
      sw.receiveShadow = true;
      sidewalks.add(sw);
    }
  }
  City.root.add(sidewalks);

  // Vehicles
  spawnVehicles();

  // People
  spawnPeople();

  // Pets
  spawnPets();

  // Rain (initialized if needed)
  updateWeather();
}

function getIntersection(i: number, j: number) {
  return City.intersections.find(p => p.i === i && p.j === j)!;
}

function nextLaneFromIntersection(at: Intersection, incomingDir: 'x'|'z'): Lane {
  const candidates = City.lanes.filter(l => l.from === at);
  // probabilistic turn
  // prefer to not reverse immediately
  const sameDir = candidates.filter(l => l.dir === incomingDir);
  const turnDir = candidates.filter(l => l.dir !== incomingDir);
  if (Math.random() < 0.7 && sameDir.length) return sameDir[Math.floor(Math.random()*sameDir.length)];
  return candidates[Math.floor(Math.random()*candidates.length)];
}

// Spawning
function spawnVehicles() {
  const total = CFG.carCount;
  const types: VehicleType[] = [];
  const svc = (f: number) => Math.floor(total * f);
  const taxis = svc(CFG.taxiFraction);
  const police = svc(CFG.policeFraction);
  const ambulances = svc(CFG.ambulanceFraction);
  const deliveries = svc(CFG.deliveryFraction);
  for (let i=0;i<taxis;i++) types.push('taxi');
  for (let i=0;i<police;i++) types.push('police');
  for (let i=0;i<ambulances;i++) types.push('ambulance');
  for (let i=0;i<deliveries;i++) types.push('delivery');
  while (types.length < total) types.push('car');

  for (const t of types) {
    const lane = City.lanes[Math.floor(Math.random() * City.lanes.length)];
    const v = new Vehicle(t, lane);
    ENTITIES.vehicles.push(v);
  }
}

function spawnPeople() {
  for (let i=0;i<CFG.pedestrianCount;i++) {
    const p = new Person();
    ENTITIES.people.push(p);
  }
}

function spawnPets() {
  for (let i=0;i<CFG.petCount;i++) {
    const master = Math.random() < 0.7 ? ENTITIES.people[Math.floor(Math.random()*ENTITIES.people.length)] : undefined;
    const pet = new Pet(master);
    ENTITIES.pets.push(pet);
  }
}

// Helpers
function findVehicleAhead(v: Vehicle, thresholdMeters: number): Vehicle | null {
  const lane = v.lane;
  const forwardPos = v.positionOnLane();
  let nearest: Vehicle | null = null;
  let nearestDist = Infinity;
  for (const other of ENTITIES.vehicles) {
    if (other === v) continue;
    if (other.lane !== lane) continue;
    if (other.t <= v.t) continue; // only vehicles ahead
    const d = (other.t - v.t) * laneLength(lane);
    if (d < thresholdMeters && d < nearestDist) { nearest = other; nearestDist = d; }
  }
  return nearest;
}

function nearestShop(pos: THREE.Vector3): { index: number, distance: number } | null {
  let best = -1; let bestDist = Infinity;
  const N = CFG.gridBlocks; const B = CFG.blockSize; const R = CFG.roadWidth; const S = CFG.sidewalkWidth;
  const citySize = N * B + (N + 1) * R;
  const start = -citySize / 2;
  let idx = 0;
  for (let i=0;i<N;i++) {
    for (let j=0;j<N;j++) {
      if (!City.shops.has(idx)) { idx++; continue; }
      const x0 = start + i*(B + R) + R;
      const z0 = start + j*(B + R) + R;
      const cx = x0 + B/2, cz = z0 + B/2;
      const d = Math.hypot(pos.x - cx, pos.z - cz);
      if (d < bestDist) { bestDist = d; best = idx; }
      idx++;
    }
  }
  if (best >= 0) return { index: best, distance: bestDist };
  return null;
}

function makeSidewalkPath(): THREE.Vector3[] {
  // pick a random block, produce rectangle loop or partial
  const N = CFG.gridBlocks; const B = CFG.blockSize; const R = CFG.roadWidth; const S = CFG.sidewalkWidth;
  const citySize = N * B + (N + 1) * R;
  const start = -citySize / 2;
  const i = Math.floor(Math.random() * N);
  const j = Math.floor(Math.random() * N);
  const x0 = start + i*(B + R) + R;
  const z0 = start + j*(B + R) + R;

  const p1 = new THREE.Vector3(x0 + S, 0, z0 + S);
  const p2 = new THREE.Vector3(x0 + B - S, 0, z0 + S);
  const p3 = new THREE.Vector3(x0 + B - S, 0, z0 + B - S);
  const p4 = new THREE.Vector3(x0 + S, 0, z0 + B - S);

  const path = [p1, p2, p3, p4];
  // maybe partial segment
  if (Math.random() < 0.5) {
    const startIdx = Math.floor(Math.random()*4);
    const len = 2 + Math.floor(Math.random()*3);
    const seg: THREE.Vector3[] = [];
    for (let k=0;k<len;k++) seg.push(path[(startIdx + k) % 4].clone());
    return seg;
  }
  return path;
}

// Selection via raycasting
const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();
renderer.domElement.addEventListener('pointerdown', (e) => {
  const rect = renderer.domElement.getBoundingClientRect();
  mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
  mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  raycaster.setFromCamera(mouse, camera);
  const objects = [
    ...ENTITIES.vehicles.map(v => v.obj),
    ...ENTITIES.people.map(p => p.obj),
    ...ENTITIES.pets.map(p => p.obj),
    ...ENTITIES.buildings,
    ...ENTITIES.parks
  ];
  const hits = raycaster.intersectObjects(objects, false);
  if (hits.length) {
    const obj = hits[0].object;
    setSelected(obj);
  }
});

function setSelected(obj: THREE.Object3D | null) {
  if (ENTITIES.selected) {
    ENTITIES.selected.traverse((o: any) => {
      if (o.material && o.material.emissive) o.material.emissive.setScalar(0);
      if (o.material && o.material.emissiveIntensity !== undefined) o.material.emissiveIntensity = 0;
    });
  }
  ENTITIES.selected = obj;
  if (obj) {
    obj.traverse((o: any) => {
      if (o.material && o.material.emissive !== undefined) {
        if (o.material.color) o.material.emissive = new THREE.Color(0x222244);
        o.material.emissiveIntensity = 0.6;
      }
    });
  }
}

// Time of day
let timeHours = 12.0;
let dayNightEffects = false; // Start with diffuse visible (no effects)
function setTimeOfDay(hours: number) {
  timeHours = hours;
  (document.getElementById('timeLabel') as HTMLSpanElement).textContent = `${hours.toFixed(1)}h`;
  
  if (!dayNightEffects) {
    // Always keep full daylight when effects are disabled (diffuse visible)
    sun.position.set(200, 400, 200);
    sun.intensity = 1.1;
    hemiLight.intensity = 0.6;
    (sky.material as THREE.ShaderMaterial).uniforms.mix.value = 0.0;
    
    // Disable building windows glow
    ENTITIES.buildings.forEach(b => {
      const mats = (b as any).material as THREE.Material | THREE.Material[];
      if (Array.isArray(mats)) {
        for (const m of mats) {
          const mat = m as any;
          if (mat.emissive !== undefined) mat.emissiveIntensity = 0;
        }
      }
    });
    return;
  }
  
  const t = hours / 24.0;
  // sun angle (0 sunrise, 0.5 sunset)
  const angle = (t * Math.PI * 2) - Math.PI/2;
  const r = 600;
  sun.position.set(Math.cos(angle) * r, Math.sin(angle) * r, Math.sin(angle) * r);
  const day = Math.max(0, Math.sin(angle));
  sun.intensity = 0.2 + day * 1.2;
  hemiLight.intensity = 0.3 + day * 0.6;
  (sky.material as THREE.ShaderMaterial).uniforms.mix.value = 1.0 - day;

  // building windows glow at night
  const night = 1.0 - day;
  ENTITIES.buildings.forEach(b => {
    const mats = (b as any).material as THREE.Material | THREE.Material[];
    if (Array.isArray(mats)) {
      for (const m of mats) {
        const mat = m as any;
        if (mat.emissive !== undefined) mat.emissiveIntensity = night * 0.7;
      }
    }
  });
}

(document.getElementById('timeOfDay') as HTMLInputElement).addEventListener('input', (e) => {
  const v = parseFloat((e.target as HTMLInputElement).value);
  setTimeOfDay(v);
});

(document.getElementById('dayNightCycle') as HTMLInputElement).addEventListener('change', (e) => {
  dayNightEffects = (e.target as HTMLInputElement).checked;
  setTimeOfDay(timeHours); // Reapply time settings with new effect state
});

// Weather
function updateWeather() {
  if (CFG.weather === 'Rain') {
    if (!ENTITIES.rain) {
      const rainGeo = new THREE.BufferGeometry();
      const count = 8000;
      const positions = new Float32Array(count * 3);
      for (let i=0;i<count;i++) {
        positions[i*3+0] = (Math.random()-0.5) * 1500;
        positions[i*3+1] = Math.random() * 400 + 50;
        positions[i*3+2] = (Math.random()-0.5) * 1500;
      }
      rainGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const rainMat = new THREE.PointsMaterial({ color: 0x9bb7ff, size: 0.6, transparent: true, opacity: 0.6 });
      const rain = new THREE.Points(rainGeo, rainMat);
      ENTITIES.rain = rain;
      scene.add(rain);
    }
  } else {
    if (ENTITIES.rain) {
      ENTITIES.rain.removeFromParent();
      ENTITIES.rain.geometry.dispose();
      (ENTITIES.rain.material as any).dispose();
      ENTITIES.rain = undefined;
    }
  }
}

// UI buttons
(document.getElementById('btnPlayPause') as HTMLButtonElement).onclick = () => {
  running = !running;
  const btn = document.getElementById('btnPlayPause') as HTMLButtonElement;
  btn.textContent = running ? '⏸️' : '▶️';
};
(document.getElementById('btnReset') as HTMLButtonElement).onclick = () => { buildCity(); };
(document.getElementById('btnFocusSel') as HTMLButtonElement).onclick = () => {
  if (ENTITIES.selected) smoothFocus(ENTITIES.selected);
};
(document.getElementById('btnSpawnEvent') as HTMLButtonElement).onclick = () => {
  spawnIncident();
};

// GUI folders
const fCity = gui.addFolder('City');
fCity.add(CFG, 'seed', 1, 9999, 1).onFinishChange(buildCity);
fCity.add(CFG, 'gridBlocks', 4, 16, 1).onFinishChange(buildCity);
fCity.add(CFG, 'blockSize', 60, 120, 1).onFinishChange(buildCity);
fCity.add(CFG, 'roadWidth', 12, 36, 1).onFinishChange(buildCity);
fCity.add(CFG, 'sidewalkWidth', 4, 12, 1).onFinishChange(buildCity);

const fPopulation = gui.addFolder('Population');
fPopulation.add(CFG, 'carCount', 20, 400, 1).onFinishChange(buildCity);
fPopulation.add(CFG, 'pedestrianCount', 0, 600, 1).onFinishChange(buildCity);
fPopulation.add(CFG, 'petCount', 0, 200, 1).onFinishChange(buildCity);
fPopulation.add(CFG, 'taxiFraction', 0, 0.5, 0.01);
fPopulation.add(CFG, 'policeFraction', 0, 0.3, 0.01);
fPopulation.add(CFG, 'ambulanceFraction', 0, 0.3, 0.01);
fPopulation.add(CFG, 'deliveryFraction', 0, 0.5, 0.01);
fPopulation.add(CFG, 'servicesEnabled');

const fSim = gui.addFolder('Simulation');
fSim.add(CFG, 'vehicleSpeed', 5, 40, 0.5);
fSim.add(CFG, 'pedestrianSpeed', 0.5, 3, 0.1);
fSim.add(CFG, 'timeScale', 0.1, 8, 0.1);
fSim.add(CFG, 'shadows').onChange((v: boolean) => { renderer.shadowMap.enabled = v; buildCity(); });
fSim.add(CFG, 'weather', ['Clear', 'Rain']).onChange(() => updateWeather());
fSim.add(CFG, 'useFlyControls').name('Fly Controls').onChange((v: boolean) => { fly.enabled = v; orbit.enabled = !v; });
fSim.add(CFG, 'following').name('Follow Selected');

const fRetro = gui.addFolder('Retro Style');
fRetro.add(CFG, 'buildingStyle', ['retro', 'modern']).onChange(buildCity);
fRetro.add(CFG, 'enablePoliceStation').onChange(buildCity);

const fDebug = gui.addFolder('Debug');
fDebug.add({ center: () => { orbit.target.set(0,0,0); } }, 'center').name('Recenter Camera');

// gui.close(); // Keep settings visible by default

// Incident spawner
function spawnIncident() {
  // pick a random intersection; make nearby police+ambulance speed up
  if (!CFG.servicesEnabled) return;
  const inter = City.intersections[Math.floor(Math.random()*City.intersections.length)];
  const p = inter.pos;
  // create blinking marker
  const marker = new THREE.Mesh(new THREE.TorusGeometry(2, 0.3, 8, 24), new THREE.MeshStandardMaterial({ color: 0xff5555, emissive: 0xff5555, emissiveIntensity: 1 }));
  marker.position.set(p.x, 0.5, p.z);
  scene.add(marker);
  setTimeout(() => { marker.removeFromParent(); marker.geometry.dispose(); (marker.material as any).dispose(); }, 6000);

  ENTITIES.vehicles.forEach(v => {
    const d = v.obj.position.distanceTo(p);
    if ((v.type === 'police' || v.type === 'ambulance') && d < 200) {
      v.speed *= 1.6;
    }
  });
}

// Camera focus
function smoothFocus(o: THREE.Object3D) {
  const box = new THREE.Box3().setFromObject(o);
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3()).length();
  const dist = Math.max(10, size * 2.5);

  const from = camera.position.clone();
  const to = center.clone().add(new THREE.Vector3(dist, dist, dist));
  const t0 = performance.now();
  const dur = 800;

  function anim() {
    const t = (performance.now() - t0) / dur;
    const k = t < 1 ? (1 - Math.cos(t*Math.PI))/2 : 1;
    camera.position.lerpVectors(from, to, k);
    orbit.target.lerpVectors(orbit.target, center, k);
    if (t < 1) requestAnimationFrame(anim);
  }
  anim();
}

// Build initial city
buildCity();
setTimeOfDay(12);

// Resize
window.addEventListener('resize', onResize);
function onResize() {
  renderer.setSize(container.clientWidth, container.clientHeight);
  camera.aspect = container.clientWidth / container.clientHeight;
  camera.updateProjectionMatrix();
}

// Main loop
const clock = new THREE.Clock();
let running = true;

function animate() {
  stats.begin();
  const dt = clock.getDelta() * CFG.timeScale;

  if (running) {
    ENTITIES.vehicles.forEach(v => v.update(dt));
    ENTITIES.people.forEach(p => p.update(dt));
    ENTITIES.pets.forEach(p => p.update(dt));

    // weather update
    if (ENTITIES.rain) {
      const pos = (ENTITIES.rain.geometry as THREE.BufferGeometry).attributes.position as THREE.BufferAttribute;
      for (let i=0;i<pos.count;i++) {
        let y = pos.getY(i);
        y -= 200 * dt;
        if (y < 0) y = Math.random()*400 + 50;
        pos.setY(i, y);
      }
      pos.needsUpdate = true;
    }

    // auto time progression (always run if simulation is running)
    if (running && CFG.timeScale > 0) {
      const timeSlider = document.getElementById('timeOfDay') as HTMLInputElement;
      const hours = parseFloat(timeSlider.value) + dt * (0.2); // slow scroll
      const h = (hours % 24 + 24) % 24;
      timeSlider.value = h.toFixed(1);
      setTimeOfDay(h);
    }
  }

  // Controls
  if (CFG.useFlyControls) {
    fly.update(dt);
  } else {
    orbit.update(); // Removed enableDamping to ensure immediate stop
  }

  // Follow selected
  if (CFG.following && ENTITIES.selected) {
    const pos = ENTITIES.selected.getWorldPosition(new THREE.Vector3());
    const desired = pos.clone().add(new THREE.Vector3(8, 6, 8));
    camera.position.lerp(desired, 0.05);
    orbit.target.lerp(pos, 0.1);
  }

  renderer.render(scene, camera);
  stats.end();
  requestAnimationFrame(animate);
}
animate();

// Utilities end

// Initial hints: Shift for fast panning
renderer.domElement.addEventListener('wheel', e => { if (e.shiftKey) orbit.panSpeed = 2.0; else orbit.panSpeed = 1.0; });

// END main.ts